import React from "react";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import {
  CodeDetection,
  ConfidenceLevels,
  VideoAnalysisService,
} from "../../../../../services/inspection/VideoAnalysisService";
import { translationService } from "../../../../../services/translation/TranslationService";
import Button from "../../../../../components/button/Button";
import { MUIComponentsTheme, useTheme } from "../../../../../services/theme-service/ThemeService";
import { Dropdown } from "../../../../../components/dropdown/Dropdown";
import { ConfidenceOptions } from "./Tools";
import { Slider, ThemeProvider } from "@mui/material";
import { videoPlayerService } from "../../../../../services/video-player/VideoPlayerService";
import CodeDetectionButton from "../../../../../components/buttons/code-detection-button/CodeDetectionButton";
import { featureFlagsStorage } from "../../../../../services/feature-flags/FeatureFlagsStorage";
import { Flags } from "../../../../../generated/FeatureFlags";
import DetectionIndivididualConfidenceModal from "./DetectionIndivididualConfidenceModal";
import Tooltip from "../../../../../components/tooltip/Tooltip";
import { AnalysisConfidenceStorage } from "../../../../../services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { Edit } from "../../../../../components/svg/svgImageList";

interface VideoToolsProps {
  videoAnalysisService: VideoAnalysisService;
  analysisConfidenceStorage: AnalysisConfidenceStorage;
}

enum VideoToolsTabs {
  Overview,
  Details,
}

export default observer(function VideoTools(props: VideoToolsProps) {
  const { videoAnalysisService, analysisConfidenceStorage } = props;
  const { themeClass } = useTheme();

  const [tabSelection, setTabSelection] = useState<VideoToolsTabs>(VideoToolsTabs.Overview);
  const isOverviewSelected = useMemo(
    () => tabSelection === VideoToolsTabs.Overview,
    [tabSelection]
  );
  const isDetailsSelected = useMemo(() => tabSelection === VideoToolsTabs.Details, [tabSelection]);

  const handleOverviewClick = useCallback(() => {
    setTabSelection(VideoToolsTabs.Overview);
    videoAnalysisService.setShowAllSegmentationBars(false);
  }, [videoAnalysisService]);

  const handleDetailsClick = useCallback(() => {
    setTabSelection(VideoToolsTabs.Details);
    videoAnalysisService.setShowAllSegmentationBars(true);
  }, [videoAnalysisService]);

  const handleConfidenceChange = useCallback(
    async (newValue: any) => {
      videoAnalysisService.setHighlightDetectionConfidenceOption(newValue.id);
      if (videoAnalysisService.firstTimeChoosingDetectionIndividualConfidence) {
        const confidenceLevels: ConfidenceLevels = {};

        if (!analysisConfidenceStorage.HighlightLevels.length) {
          await analysisConfidenceStorage.load();
        }

        analysisConfidenceStorage.HighlightLevels.forEach((code) => {
          confidenceLevels[code.name] = code.level;
        });

        videoAnalysisService.setIndividualDetectionConfidence(confidenceLevels);
      }
    },
    [analysisConfidenceStorage, videoAnalysisService]
  );

  const confidenceOptions = useMemo(() => {
    return [
      {
        id: ConfidenceOptions.Recommended,
        name: translationService.translate("Analysis.confidence-option.recommended"),
      },
      {
        id: ConfidenceOptions.Overall,
        name: translationService.translate("Analysis.confidence-option.overall"),
      },
      {
        id: ConfidenceOptions.Individual,
        name: translationService.translate("Analysis.confidence-option.individual"),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationService.language]);

  const handleOverallConfidenceChange = useCallback(
    (_: any, value: number | number[]) => {
      videoAnalysisService.setHighlightDetectionOverallConfidence((value as number) / 100);
    },
    [videoAnalysisService]
  );

  const marks = [
    {
      value: 65,
      label: "65%",
    },
    {
      value: 70,
      label: "70%",
    },
    {
      value: 75,
      label: "75%",
    },
    {
      value: 80,
      label: "80%",
    },
    {
      value: 85,
      label: "85%",
    },
    {
      value: 90,
      label: "90%",
    },
    {
      value: 95,
      label: "95%",
    },
    {
      value: 100,
      label: "100%",
    },
  ];

  const handleFilterOptionChange = (codeDetection: CodeDetection) => {
    if (!codeDetection.isDetected) return;
    const updatedOptions = videoPlayerService.groupCodeFilterOptions.map((option: CodeDetection) =>
      option.code === codeDetection.code ? { ...option, isVisible: !option.isVisible } : option
    );

    videoPlayerService.setGroupCodesFilterOptions(updatedOptions ?? []);
    videoAnalysisService?.toggleGroupCodeVisibility(codeDetection.code);
  };

  const individualConfidenceText = useMemo(() => {
    const confidenceValues = Object.values(
      videoAnalysisService.detectionIndividualConfidenceLevels
    );

    return (
      <div className="individual-confidence-container">
        <div className="individual-confidence-value">
          {confidenceValues.length !== 0 &&
            (new Set(confidenceValues).size === 1
              ? translationService.translate("analysis.individual-confidence.value", {
                  value: confidenceValues[0],
                })
              : translationService.translate("analysis.individual-confidence.values", {
                  minValue: Math.min(...confidenceValues),
                  maxValue: Math.max(...confidenceValues),
                }))}
        </div>
        <Edit
          className={themeClass(["icon-edit"])}
          onClick={() => {
            videoAnalysisService.showDetectionIndivididualConfidenceModal();
          }}
        />
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    themeClass,
    videoAnalysisService.detectionIndividualConfidenceLevels,
    translationService.language,
  ]);

  const getDisplayedDamagesInfo = () => {
    return (
      <div>
        {translationService.translate("general.damages")}
        {" ("}
        {translationService.translate("Analysis.group-codes-found-label-part1", {
          detectedDamagesCount: videoAnalysisService.detectedGroupCodes.filter((c) => c.isDetected)
            .length,
        })}
        <Tooltip
          boldedText="general.detection-classes"
          title={videoAnalysisService.detectedGroupCodes
            ?.map((groupCode) => {
              return translationService.translate(
                `damages.highlight-detection.${groupCode.code.toLowerCase()}`
              );
            })
            .join(", ")}
        >
          <span className="underlined-text">
            {translationService.translate("Analysis.group-codes-found-label-part2", {
              totalDamagesCount: videoAnalysisService.detectedGroupCodes.length,
            })}
          </span>
        </Tooltip>
        {")"}
      </div>
    );
  };

  return videoAnalysisService.isLoading ||
    !videoAnalysisService.segmentationIsAvailable ? (
    <></>
  ) : (
    <>
      <div className="tools-container">
        <div className="tools-label">{translationService.translate("Analysis.video-tools")}</div>

        <div className="tools-content-container">
          <div className="tools-section">
            <div className="buttons-container">
              <Button
                textKey="general.overview"
                onClick={handleOverviewClick}
                className={themeClass(["btn-big", isOverviewSelected ? "" : "fill-color-ghost"])}
              />
              <Button
                textKey="general.details"
                onClick={handleDetailsClick}
                className={themeClass(["btn-big", isDetailsSelected ? "" : "fill-color-ghost"])}
              />
            </div>
          </div>

          <div className="tools-section detections-with-color-container">
            {getDisplayedDamagesInfo()}
            <div className="group-codes-filter-container">
              {videoPlayerService.groupCodeFilterOptions
                .filter((x) => x.isDetected)
                .map((groupCodeOption) => (
                  <CodeDetectionButton
                    key={groupCodeOption.code}
                    codeDetection={groupCodeOption}
                    handleClick={handleFilterOptionChange}
                    isGroupCode
                  />
                ))}
            </div>
          </div>

          {featureFlagsStorage.isFeatureEnabled(Flags.Show_Confidence_Level_Controls) && (
            <div className="tools-section">
              <div className="confidence-container">
                <div className="confidence-option-container">
                  <span>{translationService.translate("Analysis.confidence")}</span>
                  <Dropdown
                    options={confidenceOptions}
                    selectedOption={
                      confidenceOptions.find(
                        (o) => o.id === videoAnalysisService.highlightDetectionConfidenceOption
                      )!
                    }
                    onChange={handleConfidenceChange}
                    fullWidth
                  />
                </div>
                {videoAnalysisService.highlightDetectionConfidenceOption ===
                  ConfidenceOptions.Overall && (
                  <div className="slider-container">
                    <ThemeProvider theme={MUIComponentsTheme}>
                      <Slider
                        key={`slider-video-tools`}
                        value={videoAnalysisService.highlightDetectionOverallConfidence * 100}
                        min={65}
                        max={100}
                        marks={marks}
                        step={5}
                        onChangeCommitted={handleOverallConfidenceChange}
                      />
                    </ThemeProvider>
                    <div className="slider-value">
                      {videoAnalysisService.highlightDetectionOverallConfidence * 100}%
                    </div>
                  </div>
                )}
                {videoAnalysisService.highlightDetectionConfidenceOption ===
                  ConfidenceOptions.Individual && individualConfidenceText}
              </div>
            </div>
          )}
        </div>
      </div>
      <DetectionIndivididualConfidenceModal
        videoAnalysisService={videoAnalysisService}
        analysisConfidenceStorage={analysisConfidenceStorage}
      />
    </>
  );
});
